import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { Linkedin, Twitter, Instagram, Mail, Phone, MapPin } from "lucide-react";

export default function Footer() {
  const { t } = useLanguage();

  const quickLinks = [
    { to: "/", label: t.nav.home },
    { to: "/services", label: t.nav.services },
    { to: "/about", label: t.nav.about },
    { to: "/contact", label: t.nav.contact },
  ];

  const legalLinks = [
    { to: "/privacy", label: t.footer.privacyPolicy },
    { to: "/cookies", label: t.footer.cookiePolicy },
    { to: "/terms", label: t.footer.termsConditions },
    { to: "/refund", label: t.footer.refundPolicy },
  ];

  return (
    <footer data-testid="main-footer" className="bg-[#1A1A1A] border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-16 sm:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          <div className="lg:col-span-1">
            <Link to="/" data-testid="footer-logo">
              <img
                src="/assets/logo-dark.png"
                alt="CREE8 Solutions"
                className="h-8 w-auto mb-6"
              />
            </Link>
            <p className="text-sm text-white/50 leading-relaxed max-w-xs">
              {t.footer.description}
            </p>
            <div className="flex gap-4 mt-6">
              <a
                href="#"
                data-testid="social-linkedin"
                className="text-white/30 hover:text-[#E8A400] transition-colors duration-300"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="#"
                data-testid="social-twitter"
                className="text-white/30 hover:text-[#E8A400] transition-colors duration-300"
                aria-label="Twitter"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a
                href="#"
                data-testid="social-instagram"
                className="text-white/30 hover:text-[#E8A400] transition-colors duration-300"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-xs tracking-[0.2em] uppercase font-semibold text-[#E8A400] mb-6">
              {t.footer.quickLinks}
            </h4>
            <nav className="flex flex-col gap-3">
              {quickLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`footer-link-${link.to.replace("/", "") || "home"}`}
                  className="text-sm text-white/50 hover:text-white transition-colors duration-300"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          <div>
            <h4 className="text-xs tracking-[0.2em] uppercase font-semibold text-[#E8A400] mb-6">
              {t.footer.legal}
            </h4>
            <nav className="flex flex-col gap-3">
              {legalLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`footer-link-${link.to.replace("/", "")}`}
                  className="text-sm text-white/50 hover:text-white transition-colors duration-300"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          <div>
            <h4 className="text-xs tracking-[0.2em] uppercase font-semibold text-[#E8A400] mb-6">
              {t.footer.connect}
            </h4>
            <div className="flex flex-col gap-4">
              <a
                href={`mailto:${t.footer.email}`}
                data-testid="footer-email"
                className="flex items-start gap-3 text-sm text-white/50 hover:text-white transition-colors duration-300"
              >
                <Mail className="w-4 h-4 mt-0.5 shrink-0 text-[#E8A400]" />
                {t.footer.email}
              </a>
              <a
                href={`tel:${t.footer.phone.replace(/\s/g, "")}`}
                data-testid="footer-phone"
                className="flex items-start gap-3 text-sm text-white/50 hover:text-white transition-colors duration-300"
              >
                <Phone className="w-4 h-4 mt-0.5 shrink-0 text-[#E8A400]" />
                {t.footer.phone}
              </a>
              <div
                data-testid="footer-address"
                className="flex items-start gap-3 text-sm text-white/50"
              >
                <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[#E8A400]" />
                <div>
                  {t.footer.address.map((line, i) => (
                    <span key={i} className="block">{line}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p data-testid="footer-copyright" className="text-xs text-white/30">
            {t.footer.copyright}
          </p>
          <p className="text-xs text-white/20">
            Cree8 Solutions LLC
          </p>
        </div>
      </div>
    </footer>
  );
}
